<template>
  <div class="page-header">
    <h1
      id="schoolyears-h1"
      class="h1"
    >
      Schuljahre
    </h1>
    <div
      class="actions"
      style="display:flex;align-items:center;gap:8px;"
    >
      <button
        class="btn btn-primary"
        aria-labelledby="schoolyears-h1"
        @click="openCreate = true"
      >
        Neu
      </button>
    </div>
  </div>

  <div class="card">
    <!-- Filterleiste -->
    <div class="filters">
      <input
        v-model="q"
        placeholder="Suchen (Schuljahr/Zeitraum)"
        autocomplete="off"
      >
      <button
        class="btn btn-primary"
        :disabled="!q"
        @click="resetFilters"
      >
        Reset
      </button>
    </div>

    <!-- Tabellen-Header -->
    <div
      class="table-header"
      style="grid-template-columns: 180px 1fr 140px 120px 100px;"
    >
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="onSort('code')"
      >
        Schuljahr
        <span v-if="sortBy === 'code'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="onSort('start_date')"
      >
        Zeitraum
        <span v-if="sortBy === 'start_date'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div># eingeschult</div>
      <div># Schüler</div>
      <div class="text-right">
        Aktionen
      </div>
    </div>

    <!-- Zeilen -->
    <div
      v-for="y in filteredItems"
      :key="y.id"
      class="table-row"
      style="grid-template-columns: 180px 1fr 140px 120px 100px;"
    >
      <div><strong>{{ y.code }}</strong></div>
      <div class="muted">
        {{ fmt(y.start_date) }} – {{ fmt(y.end_date) }}
      </div>
      <div class="muted">
        {{ y.enrolled_count ?? 0 }}
      </div>
      <div class="muted">
        {{ y.total_count ?? 0 }}
      </div>
      <div class="row-actions">
        <button
          class="icon-btn"
          title="Bearbeiten"
          aria-label="Bearbeiten"
          @click="edit(y)"
        >
          <i
            class="pi pi-pencil"
            aria-hidden="true"
          />
        </button>
        <button
          class="icon-btn danger"
          title="Löschen"
          aria-label="Löschen"
          @click="confirmDelete(y)"
        >
          <i
            class="pi pi-trash"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <p
      v-if="filteredItems.length === 0"
      class="muted"
    >
      Keine Einträge.
    </p>

    <!-- Pagination -->
    <div
      class="mt-12"
      style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
    >
      <span
        class="muted"
        style="margin-right:auto"
      >Gesamt: {{ total }}</span>
      <label
        class="muted"
        for="sy-limit"
      >pro Seite:</label>
      <select
        id="sy-limit"
        :value="limit"
        @change="changeLimit"
      >
        <option
          v-for="n in [10,20,50,100]"
          :key="n"
          :value="n"
        >
          {{ n }}
        </option>
      </select>
      <button
        class="btn"
        :disabled="page<=1"
        @click="prevPage"
      >
        Zurück
      </button>
      <span class="muted">Seite {{ page }} / {{ pages }}</span>
      <button
        class="btn"
        :disabled="page>=pages"
        @click="nextPage"
      >
        Weiter
      </button>
    </div>
  </div>

  <!-- Create/Edit Dialog -->
  <BaseDialog
    :open="openCreate || !!current"
    :disabled="loading"
    @close="closeDialog"
  >
    <template #title>
      {{ current ? 'Schuljahr bearbeiten' : 'Neues Schuljahr anlegen' }}
    </template>
    <form @submit.prevent="submit">
      <div class="form">
        <div class="two-col">
          <div class="field">
            <label for="start">Startdatum</label>
            <Calendar
              id="start"
              v-model="startAt"
              :manual-input="false"
              date-format="dd.mm.yy"
              :show-icon="true"
              :touch-u-i="true"
            />
            <p
              v-if="errors.startDate"
              class="help danger"
              role="alert"
            >
              {{ errors.startDate }}
            </p>
          </div>
          <div class="field">
            <label for="end">Enddatum</label>
            <Calendar
              id="end"
              v-model="endAt"
              :manual-input="false"
              date-format="dd.mm.yy"
              :show-icon="true"
              :touch-u-i="true"
            />
            <p
              v-if="errors.endDate"
              class="help danger"
              role="alert"
            >
              {{ errors.endDate }}
            </p>
          </div>
        </div>
      </div>

      <div class="field">
        <label for="code">Schuljahr</label>
        <input
          id="code"
          v-model="form.code"
          readonly
          disabled
          autocomplete="off"
        >
        <p
          v-if="errors.code"
          class="help danger"
          role="alert"
        >
          {{ errors.code }}
        </p>
      </div>
      <p
        v-if="errorMsg"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ errorMsg }}
      </p>
      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="closeDialog"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Speichern
        </button>
      </div>
    </form>
  </BaseDialog>

  <!-- Delete Dialog -->
  <BaseDialog
    :open="!!toDelete"
    :disabled="loading"
    @close="toDelete=null"
  >
    <template #title>
      Schuljahr löschen?
    </template>
    <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
    <div
      class="mt-12"
      style="display:flex;gap:8px;justify-content:flex-end;"
    >
      <button
        class="btn"
        :disabled="loading"
        @click="toDelete=null"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-primary"
        style="background:var(--danger);border-color:var(--danger)"
        :disabled="loading"
        @click="doDelete"
      >
        <span
          v-if="loading"
          class="spinner"
          aria-hidden="true"
        /> Löschen
      </button>
    </div>
  </BaseDialog>

  <!-- Top-Navigation: Hilfe-Button -->
  <teleport to=".top-nav">
    <span
      id="top-nav-help-schoolyears"
      class="top-nav-help"
    >
      <UiInfoButton title="Hilfe: Schuljahre">
        <ul>
          <li>Suche nach Schuljahr oder Zeitraum.</li>
          <li>Spalten: Schuljahrscode (z. B. 2024/2025), Zeitraum, Anzahl eingeschulter Schüler und Gesamtzahl.</li>
          <li>Aktionen: Bearbeiten und Löschen eines Schuljahrs.</li>
          <li>Dialog: Start- und Enddatum per Kalender wählen; der Code wird automatisch aus dem Startjahr berechnet.</li>
          <li>Validierung: Das Enddatum muss nach dem Startdatum liegen und im Folgejahr enden.</li>
        </ul>
      </UiInfoButton>
    </span>
  </teleport>
</template>

<script setup>
import {onMounted, onBeforeUnmount, ref, watch, computed} from 'vue';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';
import BaseDialog from '@/components/BaseDialog.vue';
import UiInfoButton from '@/components/UiInfoButton.vue';

const items = ref([]);
const loading = ref(false);
const openCreate = ref(false);
const current = ref(null);
const toDelete = ref(null);
const toast = useToast();

// Sortierung (initial: nach Schuljahr absteigend)

// Filter
const q = ref('');
const filteredItems = computed(() => {
  const term = q.value.trim().toLowerCase();
  if (!term) return items.value;
  return items.value.filter(row => {
    const code = (row.code || '').toString().toLowerCase();
    const period = `${fmt(row.start_date)} – ${fmt(row.end_date)}`.toLowerCase();
    return code.includes(term) || period.includes(term);
  });
});

function resetFilters() {
  q.value = '';
}

// Sortierung (initial nach Schuljahr absteigend)
const sortBy = ref('code');
const sortDir = ref('desc');

// Sortierung (initial: nach Schuljahr absteigend) – gesteuert über sortBy/sortDir

// Pagination
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const pages = ref(0);

const form = ref({code: '', start_date: '', end_date: ''});
const errors = ref({});
const errorMsg = ref('');

// Date-Objekte für PrimeVue Calendar
const startAt = ref(null);
const endAt = ref(null);

function fmt(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function formatDe(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function recomputeAndValidate() {
  // Strings für Submit aktualisieren
  if (startAt.value instanceof Date) {
    form.value.start_date = formatDe(startAt.value);
  } else {
    form.value.start_date = '';
  }
  if (endAt.value instanceof Date) {
    form.value.end_date = formatDe(endAt.value);
  } else {
    form.value.end_date = '';
  }

  // Code automatisch aus Startjahr berechnen
  if (startAt.value instanceof Date) {
    const sy = startAt.value.getFullYear();
    form.value.code = `${sy}/${sy + 1}`;
  } else {
    form.value.code = '';
  }

  // Fehler zurücksetzen
  errors.value = {...errors.value, start_date: undefined, end_date: undefined};

  if (startAt.value instanceof Date && endAt.value instanceof Date) {
    if (endAt.value.getTime() <= startAt.value.getTime()) {
      errors.value.end_date = 'Muss nach dem Startdatum liegen';
    }
    const startYear = startAt.value.getFullYear();
    const endYear = endAt.value.getFullYear();
    if (endYear !== startYear + 1) {
      errors.value.end_date = `Endjahr muss ${startYear + 1} sein`;
    }
  }
}

watch(startAt, recomputeAndValidate);
watch(endAt, recomputeAndValidate);

async function load() {
  loading.value = true;
  const {data} = await api.get('/school-years', {
    params: {
      page: page.value,
      limit: limit.value,
      sort: sortBy.value,
      dir: sortDir.value
    }
  });
  items.value = data.items || [];
  total.value = data.total || 0;
  pages.value = data.pages || 0;
  loading.value = false;
}

function nextPage() {
  if (page.value < pages.value) {
    page.value++;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--;
    load();
  }
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
  load();
}

function onSort(column) {
  if (sortBy.value === column) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = column;
    // Standard: Code absteigend, Zeitraum (start_date) aufsteigend initial
    sortDir.value = column === 'code' ? 'desc' : 'asc';
  }
  page.value = 1;
  load();
}

function edit(row) {
  current.value = row;
  // Date-Objekte für Calendar setzen; Strings werden vom Watcher erzeugt
  startAt.value = new Date(row.start_date + 'T00:00:00');
  endAt.value = new Date(row.end_date + 'T00:00:00');
  form.value.code = row.code;
  recomputeAndValidate();
}

function closeDialog() {
  openCreate.value = false;
  current.value = null;
  startAt.value = null;
  endAt.value = null;
  form.value = {code: '', startDate: '', endDate: ''};
  errors.value = {};
  errorMsg.value = '';
}

async function submit() {
  loading.value = true;
  errors.value = {};
  errorMsg.value = '';

  try {
    if (current.value) {
      await api.put(`/school-years/${current.value.id}`, form.value);
    } else {
      await api.post('/school-years', form.value);
    }
    closeDialog();
    // Liste mit aktiven Filter-/Sortier-/Pagination-Parametern neu laden
    await load();
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const res = e.response?.data;
    if (res?.error?.details?.fields) {
      errors.value = res.error.details.fields;
    } else {
      errorMsg.value = res?.error?.message || 'Aktion fehlgeschlagen.';
    }
  } finally {
    loading.value = false;
  }
}

function confirmDelete(row) {
  toDelete.value = row;
}

async function doDelete() {
  loading.value = true;
  try {
    await api.delete(`/school-years/${toDelete.value.id}`);
    items.value = items.value.filter(i => i.id !== toDelete.value.id);
    toDelete.value = null;
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
  } catch (e) {
    toDelete.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(load);

// Hilfe-Button vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-schoolyears');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-schoolyears');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});
</script>

<style scoped>
/* Dialog-spezifisch */
.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.table-row {
  padding: 5px 0;
  font-size: 14px;
}
</style>
